import { useEffect, useMemo, useState } from 'react';

import {
  Box,
  Container,
  Pagination,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMutation, useQuery } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';

import HeaderArticles from './HeaderArticles';
import SingleArticle from './SingleArticle';
import MetaTags from '../../components/Seo/MetaTags';
import SkeletonGrid from '../../components/SkeletonGrid';
import Subheader from '../../components/Subheader';
import { useAuthContext } from '../../context/AuthContextProvider';
import { filterArticles } from '../../services/elastic';
import { getInformativeTextByLocation } from '../../services/informative';
import { truncateText } from '../../utils/string';

const LIMIT = 20;
const FEATURED_LIMIT = 5;
const MAIN_FEATURED_SUMMARY_LIMIT = 220;
const CARD_SUMMARY_LIMIT = 120;
const DEFAULT_SORT = 'desc';

const URL_ARRAY_FILTERS = [
  ['categories', 'categories'],
  ['markets', 'markets'],
  ['institutions', 'relatedInstitutions'],
  ['professionals', 'relatedProfessionals'],
  ['operations', 'relatedOperations'],
];

const URL_STATE_PARAMS = [
  'page',
  'sort',
  'q',
  'startDate',
  'endDate',
  ...URL_ARRAY_FILTERS.map(([paramName]) => paramName),
];

const getArrayParam = (params, key) =>
  params
    .getAll(key)
    .flatMap((value) => value.split(','))
    .map((value) => value.trim())
    .filter(Boolean);

const getDateRangeFromParams = (params) => {
  const start = params.get('startDate');
  const end = params.get('endDate');

  if (!start && !end) return undefined;

  return {
    start: start ? new Date(`${start}T00:00:00`).toISOString() : null,
    end: end ? new Date(`${end}T00:00:00`).toISOString() : null,
  };
};

const getInitialPage = (params) => {
  const page = Number(params.get('page'));
  return Number.isInteger(page) && page > 0 ? page : 1;
};

const getInitialSort = (params) =>
  params.get('sort') ?? (params.get('q') ? 'relevance' : DEFAULT_SORT);

const clearUrlStateParams = ({ history, location }) => {
  const params = new URLSearchParams(location.search);
  let hasStateParams = false;

  URL_STATE_PARAMS.forEach((paramName) => {
    if (params.has(paramName)) {
      hasStateParams = true;
      params.delete(paramName);
    }
  });

  if (hasStateParams) {
    const nextSearch = params.toString();

    history.replace({
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : '',
      state: location.state,
    });
  }
};

const getInitialFilters = (search, fallbackInstitutionName) => {
  const params = new URLSearchParams(search);
  const filters = {
    keyword: params.get('q') ?? '',
    categories: [],
    markets: [],
    relatedInstitutions: fallbackInstitutionName
      ? [fallbackInstitutionName]
      : [],
    relatedProfessionals: [],
    relatedOperations: [],
  };

  URL_ARRAY_FILTERS.forEach(([paramName, filterName]) => {
    const values = getArrayParam(params, paramName);
    if (values.length > 0) {
      filters[filterName] = values;
    }
  });

  const dateRange = getDateRangeFromParams(params);
  if (dateRange) filters.dateRange = dateRange;

  return filters;
};

const stripHtml = (value) =>
  value
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const normalizeText = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return stripHtml(value);
  if (Array.isArray(value)) {
    return value.map(normalizeText).filter(Boolean).join(' ');
  }
  if (typeof value === 'object') {
    return normalizeText(
      value.text ?? value.children ?? value.content ?? value.value ?? '',
    );
  }

  return String(value);
};

const resolveArticleSummary = ({ lide, description, introtext }, limit) => {
  const summary = [lide, description, introtext]
    .map(normalizeText)
    .find(Boolean);

  return summary ? truncateText(summary, limit) : '';
};

const getFeaturedArticlesPayload = () => ({
  data: {
    filters: {
      featured: true,
    },
    from: 0,
    limit: FEATURED_LIMIT,
    sort: DEFAULT_SORT,
  },
});

const syncUrlState = ({ filters, history, location, page, sort }) => {
  const params = new URLSearchParams(location.search);

  ['page', 'sort', 'q', 'startDate', 'endDate'].forEach((paramName) => {
    params.delete(paramName);
  });
  URL_ARRAY_FILTERS.forEach(([paramName]) => params.delete(paramName));

  if (page > 1) params.set('page', String(page));
  if (sort !== DEFAULT_SORT) params.set('sort', sort);
  if (filters.keyword) params.set('q', filters.keyword);

  URL_ARRAY_FILTERS.forEach(([paramName, filterName]) => {
    filters[filterName]?.forEach((value) => params.append(paramName, value));
  });

  if (filters.dateRange?.start) {
    params.set('startDate', filters.dateRange.start.slice(0, 10));
  }
  if (filters.dateRange?.end) {
    params.set('endDate', filters.dateRange.end.slice(0, 10));
  }

  const nextSearch = params.toString();
  const currentSearch = location.search.replace(/^\?/, '');

  if (nextSearch !== currentSearch) {
    history.replace({
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : '',
      state: location.state,
    });
  }
};

export default function AllArticles() {
  const { auth } = useAuthContext();
  const history = useHistory();
  const location = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const institutionName = searchParams.get('instituicao');
  const shouldUseUrlState = auth.isLoggedIn;
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const [page, setPage] = useState(() =>
    shouldUseUrlState ? getInitialPage(searchParams) : 1,
  );
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState(() =>
    shouldUseUrlState ? getInitialSort(searchParams) : DEFAULT_SORT,
  );
  const [filters, setFilters] = useState(() =>
    shouldUseUrlState
      ? getInitialFilters(location.search, institutionName)
      : getInitialFilters('', null),
  );
  const [articles, setArticles] = useState([]);
  const [scroll, setScroll] = useState(false);
  const from = (page - 1) * LIMIT;

  const { data } = useQuery('info', () =>
    getInformativeTextByLocation('article'),
  );
  const {
    data: featuredArticlesResponse,
    isLoading: loadingFeaturedArticles,
    isError: isFeaturedArticlesError,
  } = useQuery('featuredArticles', () =>
    filterArticles(getFeaturedArticlesPayload()),
  );

  const filteredArticles = useMutation(filterArticles, {
    onSuccess: ({ data }) => {
      setArticles(data.data ?? []);
      setTotal(data.total ?? 0);
    },
  });

  const payload = useMemo(
    () => ({ data: { filters, from, sort } }),
    [filters, from, sort],
  );

  useEffect(() => {
    filteredArticles.mutate(payload);
  }, [payload]);

  useEffect(() => {
    if (!shouldUseUrlState) {
      clearUrlStateParams({ history, location });
      return;
    }

    syncUrlState({ filters, history, location, page, sort });
  }, [filters, history, location, page, shouldUseUrlState, sort]);

  useEffect(() => {
    window.scrollTo({
      top: isSmall ? 760 : 0,
      behavior: 'smooth',
    });
  }, [isSmall, scroll]);

  const { isLoading: loading, isError } = filteredArticles;
  const featuredArticles =
    featuredArticlesResponse?.data?.data?.slice(0, FEATURED_LIMIT) ?? [];
  const mainFeaturedArticle = featuredArticles[0] ?? null;
  const secondaryFeaturedArticles = featuredArticles.slice(1);
  const hasOnlyOneFeaturedArticle = featuredArticles.length === 1;
  const hasDenseFeaturedSidebar = secondaryFeaturedArticles.length >= 4;
  const listedArticles = articles ?? [];
  const totalPages = Math.ceil(total / LIMIT);

  const onChangePagination = (_, value) => {
    setPage(value);
    setScroll((prevState) => !prevState);
  };

  const handleSetFilters = (nextFilters) => {
    setPage(1);
    setFilters((currentFilters) =>
      typeof nextFilters === 'function'
        ? nextFilters(currentFilters)
        : nextFilters,
    );
  };

  const handleSetSort = (nextSort) => {
    setPage(1);
    setSort(nextSort);
  };

  return (
    <Box component="section" sx={{ backgroundColor: '#FAFAFA', pb: 6 }}>
      <MetaTags
        title="Artigos / Análise"
        description="Acesse diariamente inteligência de mercado sobre FII, FIDC, CRI e CRA, além de encontrar acervo com mais de 3 mil artigos sobre estes mercados."
      />
      <Subheader
        title="Artigos"
        breadcrumbs1="Artigos"
        tooltipText={data?.data?.article?.informationText}
      />

      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 } }}>
        {loadingFeaturedArticles ? (
          <Stack spacing={2} mb={3}>
            <Box>
              <Typography component="h2" variant="h5" fontWeight={900}>
                Artigos em destaque
              </Typography>
              <Typography color="text.secondary" variant="body2">
                Uma curadoria inicial para começar a leitura.
              </Typography>
            </Box>
            <SkeletonGrid
              height="250px"
              width="auto"
              skeletonNumber={FEATURED_LIMIT}
              sm={12}
              md={6}
              lg={6}
            />
          </Stack>
        ) : !isFeaturedArticlesError && mainFeaturedArticle ? (
          <Stack spacing={2} mb={3}>
            <Box>
              <Typography component="h2" variant="h5" fontWeight={900}>
                Artigos em destaque
              </Typography>
              <Typography color="text.secondary" variant="body2">
                Uma curadoria inicial para começar a leitura.
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gap: 2.5,
                gridTemplateColumns: {
                  xs: '1fr',
                  md: hasOnlyOneFeaturedArticle
                    ? '1fr'
                    : 'minmax(0, 7fr) minmax(300px, 5fr)',
                  lg: hasOnlyOneFeaturedArticle
                    ? '1fr'
                    : 'minmax(0, 8fr) minmax(320px, 4fr)',
                },
                alignItems: 'stretch',
              }}>
              <Box minWidth={0}>
                <SingleArticle
                  featured
                  id={mainFeaturedArticle.id}
                  name={
                    mainFeaturedArticle?.name ??
                    mainFeaturedArticle?.title ??
                    ''
                  }
                  markets={mainFeaturedArticle?.markets ?? []}
                  publishedAt={mainFeaturedArticle?.publishedAt}
                  slug={mainFeaturedArticle.slug}
                  headerImage={mainFeaturedArticle?.headerImage}
                  plans={mainFeaturedArticle?.plans ?? []}
                  featuredLayout={
                    hasOnlyOneFeaturedArticle ? 'fullWidth' : 'default'
                  }
                  summary={resolveArticleSummary(
                    mainFeaturedArticle,
                    MAIN_FEATURED_SUMMARY_LIMIT,
                  )}
                />
              </Box>
              {secondaryFeaturedArticles.length > 0 && (
                <Box minWidth={0}>
                  <Box
                    sx={{
                      display: 'grid',
                      gap: hasDenseFeaturedSidebar ? 1.25 : 2,
                      height: '100%',
                      gridTemplateRows: {
                        xs: 'auto',
                        md: `repeat(${secondaryFeaturedArticles.length}, minmax(0, 1fr))`,
                      },
                    }}>
                    {secondaryFeaturedArticles.map((article) => (
                      <SingleArticle
                        compact
                        key={article.id}
                        compactSize={
                          secondaryFeaturedArticles.length === 1
                            ? 'tall'
                            : 'default'
                        }
                        compactDensity={
                          hasDenseFeaturedSidebar ? 'dense' : 'default'
                        }
                        id={article.id}
                        name={article.name ?? article.title}
                        markets={article?.markets}
                        publishedAt={article?.publishedAt}
                        slug={article.slug}
                        headerImage={article?.headerImage}
                        plans={article?.plans}
                        summary={resolveArticleSummary(
                          article,
                          CARD_SUMMARY_LIMIT,
                        )}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Stack>
        ) : null}

        <HeaderArticles
          filters={filters}
          resultCount={total}
          sort={sort}
          setSort={handleSetSort}
          setFilters={handleSetFilters}
        />

        <Box mb={2}>
          <Typography component="h2" variant="h5" fontWeight={900}>
            Todos os artigos
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {totalPages > 1
              ? `Página ${page} de ${totalPages}`
              : 'Explore a seleção completa de conteúdos disponíveis.'}
          </Typography>
        </Box>

        {loading ? (
          <SkeletonGrid
            height="250px"
            width="auto"
            skeletonNumber={LIMIT}
            sm={12}
            md={12}
            lg={12}
          />
        ) : isError ? (
          <Paper
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'grey.200',
              borderRadius: 4,
              p: 4,
              textAlign: 'center',
            }}>
            <Typography variant="h6" fontWeight={800} gutterBottom>
              Não foi possível carregar os artigos
            </Typography>
            <Typography color="text.secondary">
              Tente novamente em alguns instantes ou revise os filtros
              aplicados.
            </Typography>
          </Paper>
        ) : articles.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'grey.200',
              borderRadius: 4,
              p: 5,
              textAlign: 'center',
            }}>
            <Typography variant="h5" fontWeight={900} gutterBottom>
              Nenhum artigo encontrado
            </Typography>
            <Typography color="text.secondary">
              Ajuste os filtros ou tente uma busca com termos mais amplos.
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={3}>
            {listedArticles.map(
              ({
                id,
                name,
                markets,
                publishedAt,
                slug,
                headerImage,
                plans = [],
                lide,
                description,
                introtext,
              }) => (
                <SingleArticle
                  key={id}
                  id={id}
                  name={name}
                  markets={markets}
                  publishedAt={publishedAt ?? ''}
                  slug={slug}
                  headerImage={headerImage}
                  plans={plans}
                  summary={resolveArticleSummary(
                    { lide, description, introtext },
                    CARD_SUMMARY_LIMIT,
                  )}
                />
              ),
            )}
          </Stack>
        )}

        {totalPages > 1 && (
          <Box pt={4} display="flex" justifyContent="center">
            <Pagination
              count={totalPages}
              page={page}
              onChange={onChangePagination}
              shape="rounded"
              siblingCount={isSmall ? 0 : 1}
              size="large"
              color="primary"
            />
          </Box>
        )}
      </Container>
    </Box>
  );
}
